import type { DefaultError, QueryKey } from '@tanstack/query-core';
import { UseSuspenseInfiniteQueryOptions, useSuspenseInfiniteQuery } from '@tanstack/react-query';
import { FetchAdvancedEmailTemplateParams, fetchAdvancedEmailTemplates } from 'api/engage';
import { useCurrentAccount } from 'customHooks/useCurrentAccount';
import { useInfiniteScroll } from 'customHooks/useInfiniteScroll';
import { accountQueryKeys } from 'queries/account-query-keys';
import { AdvancedMessageTemplatesResponse, EngageMessageTemplate } from 'types/domain';

const DEFAULT_PAGE_LIMIT = 20;

interface Options
    extends Omit<
        UseSuspenseInfiniteQueryOptions<
            AdvancedMessageTemplatesResponse,
            DefaultError,
            Pick<
                EngageMessageTemplate,
                'id' | 'preview_image' | 'name' | 'description' | 'created_at' | 'channel'
            >,
            AdvancedMessageTemplatesResponse,
            QueryKey,
            number
        >,
        'queryKey' | 'initialPageParam' | 'select' | 'getNextPageParam'
    > {
    enabled?: boolean;
}

const useInfiniteAdvancedEmailTemplates = (
    params: Omit<FetchAdvancedEmailTemplateParams, 'page'> = {},
    options: Options = {} as Options,
) => {
    const { limit = DEFAULT_PAGE_LIMIT, ...rest } = params;
    const nextParams = { limit, ...rest };
    const { accountId } = useCurrentAccount();
    const infiniteQueryData = useSuspenseInfiniteQuery({
        initialPageParam: 1,
        queryKey: accountQueryKeys.engageAdvancedMessageTemplatesWithFilters(accountId, nextParams),
        queryFn: ({ pageParam }) => fetchAdvancedEmailTemplates({ page: pageParam, ...nextParams }),
        select: data => {
            return {
                ...data,
                pages: data.pages
                    .flatMap(d => d.results)
                    .filter(d => !d.is_blank)
                    .map(d => ({
                        id: d.id,
                        preview_image: d.thumbnail,
                        name: d.title,
                        description: d.short_description,
                        created_at: d.published_at,
                        channel: 'email',
                    })),
            };
        },
        getNextPageParam: lastPage => {
            if (!lastPage.next) return undefined;
            const pageParam = new URL(lastPage.next).searchParams.get('page');
            return pageParam ? parseInt(pageParam) : undefined;
        },
        refetchOnWindowFocus: false,
        ...options,
    });
    const ref = useInfiniteScroll(infiniteQueryData);

    return { ...infiniteQueryData, ref };
};

export { useInfiniteAdvancedEmailTemplates };
